import { useState } from "react";
import { Banknote, ChevronDown, ChevronRight, Gift, HandCoins, Wallet } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table.js";
import { Button } from "./ui/button.js";
import { Checkbox } from "./ui/checkbox.js";
import { Label } from "./ui/label.js";
import { SummaryStatCard, SummaryStatSuffix } from "./SummaryStatCard.js";
import type { FloodCardTone } from "./ui/flood-card.js";
import { cn } from "../lib/utils.js";
import {
  COMP_LABELS, DED_KEYS, EARN_KEYS, EMP_KEYS, amountOf, asNum, fmtAmt, fmtDate, takeHomeBeforeTax, totalsOf,
  type BreakdownAmount,
} from "../lib/compensation.js";

export interface CompareSide {
  ctcAnnual:     number | string;
  effectiveFrom: string;
  breakdown:     BreakdownAmount[];
}

interface Props {
  /** The figures being replaced; null when this is the employee's first revision. */
  before: CompareSide | null;
  after:  CompareSide;
  beforeLabel?: string;
  afterLabel?:  string;
}

const GROUPS: [string, string[]][] = [
  ["Earnings", EARN_KEYS],
  ["Deductions", DED_KEYS],
  ["Employer cost", EMP_KEYS],
];

/** "+₹10,000 (+66.7%)", or null when there is nothing to compare or no change. */
function changeText(before: number | null, after: number): { text: string; up: boolean } | null {
  if (before === null) return null;
  const diff = after - before;
  if (Math.round(diff) === 0) return null;
  const pct = before !== 0 ? ` (${diff > 0 ? "+" : "−"}${Math.abs((diff / before) * 100).toFixed(1)}%)` : "";
  return { text: `${diff > 0 ? "+" : "−"}₹${fmtAmt(Math.abs(diff))}${pct}`, up: diff > 0 };
}

function Change({ before, after }: { before: number | null; after: number }) {
  if (before === null) return <span className="text-surface-400">New</span>;
  const c = changeText(before, after);
  if (!c) return <span className="text-surface-400">—</span>;
  return <span className={cn("font-semibold", c.up ? "text-[var(--color-success-fg)]" : "text-[var(--color-error-fg)]")}>{c.text}</span>;
}

function TotalTile({ icon, label, tone, before, after, monthly = false }: {
  icon: typeof Wallet; label: string; tone: FloodCardTone; before: number | null; after: number;
  /** Also show the per-month figure (salary and take-home; not CTC or the yearly bonus). */
  monthly?: boolean;
}) {
  const c = changeText(before, after);
  // Concise caption: the monthly figure where it applies, then the direction and % change.
  // The exact rupee figures are in the comparison table below. Each tile keeps a fixed colour
  // of its own (tinted at rest), none of them green/amber/red, so colour never reads as "raise" or "cut".
  const pct = before && before !== 0 ? Math.abs(((after - before) / before) * 100).toFixed(1) : null;
  const change = before === null ? "New" : c && pct ? `${c.up ? "▲" : "▼"} ${pct}%` : "No change";
  return (
    <SummaryStatCard
      icon={icon}
      tone={tone}
      layout="row" // compact: icon + label/value on one line, caption on the right
      wrapLabel    // long labels like "Gross pay (excl. bonus)" show in full
      card={{ surface: "tinted" }}
      label={label}
      value={<>
        ₹{fmtAmt(after)}<SummaryStatSuffix> /yr</SummaryStatSuffix>
        {/* The monthly figure sits on its own line under the yearly one. */}
        {monthly && <span className="block leading-tight"><SummaryStatSuffix>₹{fmtAmt(after / 12)} /mo</SummaryStatSuffix></span>}
      </>}
      caption={change}
      className="flex-[1_1_200px]"
    />
  );
}

/** Previous vs revised compensation: headline totals, then the components that changed. */
export function CompCompareTable({ before, after, beforeLabel = "Previous", afterLabel = "Revised" }: Props) {
  const [showUnchanged, setShowUnchanged] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const valueOf = (side: CompareSide | null, k: string) => (side ? amountOf(side.breakdown, k) : null);
  // Proposed monthly figure, as stored on the breakdown line.
  const monthlyOf = (k: string) => asNum(after.breakdown.find(l => l.componentKey === k)?.monthlyAmount);
  // Component + [previous yearly] + proposed yearly + proposed monthly + [change].
  const colCount = before ? 5 : 3;

  // Final totals under the table. Gross pay excludes the bonus (paid yearly, shown on its own row).
  const grossExBonus = (side: CompareSide) => totalsOf(side.breakdown).gross - amountOf(side.breakdown, "BONUS");
  // Same take-home formula as the drawer and profile (bonus excluded — it is paid yearly).
  const inHand = (side: CompareSide) => takeHomeBeforeTax(side.breakdown);
  const hasBonus = amountOf(after.breakdown, "BONUS") > 0 || (!!before && amountOf(before.breakdown, "BONUS") > 0);
  const totalRows: { label: string; before: number | null; after: number; monthly: string }[] = [
    { label: "Gross pay (excl. bonus)", before: before ? grossExBonus(before) : null, after: grossExBonus(after), monthly: `₹${fmtAmt(grossExBonus(after) / 12)}` },
    { label: "Bonus (paid yearly)",     before: before ? amountOf(before.breakdown, "BONUS") : null, after: amountOf(after.breakdown, "BONUS"), monthly: "Paid yearly" },
    { label: "Total CTC",               before: before ? asNum(before.ctcAnnual) : null, after: asNum(after.ctcAnnual), monthly: "—" },
    { label: "In-hand (before tax)",    before: before ? inHand(before) : null, after: inHand(after), monthly: `₹${fmtAmt(inHand(after) / 12)}` },
  ];
  const isShown = (k: string) => {
    if (k === "BONUS") return true; // always listed so it is clear whether a bonus applies, even at ₹0
    const b = valueOf(before, k);
    const a = amountOf(after.breakdown, k);
    if (a === 0 && !b) return false;
    return showUnchanged || before === null || Math.round(a - (b ?? 0)) !== 0;
  };
  const hiddenCount = GROUPS.flatMap(([, keys]) => keys)
    .filter(k => (amountOf(after.breakdown, k) !== 0 || !!valueOf(before, k)) && !isShown(k)).length;
  // How many line items differ — shown on the toggle so the detail is findable without being open.
  const changedCount = before
    ? GROUPS.flatMap(([, keys]) => keys).filter(k => Math.round(amountOf(after.breakdown, k) - (valueOf(before, k) ?? 0)) !== 0).length
    : 0;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-2">
        {/* Salary figures exclude the bonus (it is less from CTC and paid yearly), so the monthly
            salary is visible on its own; the bonus gets its own tile when there is one. */}
        <TotalTile icon={Wallet}    label="Total CTC"            tone="info"   before={before ? asNum(before.ctcAnnual) : null} after={asNum(after.ctcAnnual)} />
        <TotalTile icon={HandCoins} label="Gross pay (excl. bonus)" tone="accent" before={before ? grossExBonus(before) : null} after={grossExBonus(after)} monthly />
        <TotalTile icon={Banknote}  label="Take home (before tax)" tone="cyan"   before={before ? inHand(before) : null} after={inHand(after)} monthly />
        {hasBonus && (
          <TotalTile icon={Gift}    label="Bonus (paid yearly)"  tone="info"   before={before ? amountOf(before.breakdown, "BONUS") : null} after={amountOf(after.breakdown, "BONUS")} />
        )}
      </div>

      <Button
        type="button" variant="ghost" size="sm"
        className="self-start gap-1 px-0 text-brand-600"
        onClick={() => setShowDetails(v => !v)}
        aria-expanded={showDetails}
      >
        {showDetails ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        {showDetails ? "Hide" : "View"} component breakdown
        {before && changedCount > 0 && <span className="font-normal text-surface-500">· {changedCount} changed</span>}
      </Button>

      {!showDetails ? null : (
      <>
      {before && (
        <div className="flex items-center gap-2">
          <Checkbox id="comp-compare-unchanged" checked={showUnchanged} onCheckedChange={v => setShowUnchanged(v === true)} />
          <Label htmlFor="comp-compare-unchanged" className="text-[12px] font-normal text-surface-600">
            Show unchanged components{!showUnchanged && hiddenCount > 0 && ` (${hiddenCount} hidden)`}
          </Label>
        </div>
      )}

      <div className="overflow-x-auto rounded-md border border-surface-200">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Component</TableHead>
              {before && (
                <TableHead className="text-right">
                  {beforeLabel} · yearly<div className="font-normal normal-case tracking-normal">from {fmtDate(before.effectiveFrom)}</div>
                </TableHead>
              )}
              <TableHead className="text-right">
                {afterLabel} · yearly<div className="font-normal normal-case tracking-normal">from {fmtDate(after.effectiveFrom)}</div>
              </TableHead>
              <TableHead className="text-right">
                {afterLabel} · monthly<div className="font-normal normal-case tracking-normal">per month</div>
              </TableHead>
              {before && <TableHead className="text-right">Change (yearly)</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {GROUPS.map(([title, keys]) => {
              const shown = keys.filter(isShown);
              if (shown.length === 0) return null;
              return [
                <TableRow key={title}>
                  <TableCell colSpan={colCount} className="px-3 pt-3 text-[10px] font-bold uppercase tracking-[0.06em] text-surface-500">{title}</TableCell>
                </TableRow>,
                ...shown.map(k => {
                  const b = valueOf(before, k);
                  const a = amountOf(after.breakdown, k);
                  return (
                    <TableRow key={k}>
                      <TableCell className="px-3">{COMP_LABELS[k] ?? k}</TableCell>
                      {before && <TableCell className="px-3 text-right tabular-nums">₹{fmtAmt(b ?? 0)}</TableCell>}
                      <TableCell className="px-3 text-right tabular-nums">₹{fmtAmt(a)}</TableCell>
                      <TableCell className="px-3 text-right tabular-nums">
                        {/* Bonus is paid once a year, never monthly. */}
                        {k === "BONUS" ? <span className="text-surface-400">Paid yearly</span> : `₹${fmtAmt(monthlyOf(k))}`}
                      </TableCell>
                      {before && <TableCell className="px-3 text-right tabular-nums"><Change before={b} after={a} /></TableCell>}
                    </TableRow>
                  );
                }),
              ];
            })}
            {hiddenCount > 0 && GROUPS.every(([, keys]) => keys.filter(isShown).length === 0) && (
              <TableRow>
                <TableCell colSpan={colCount} className="px-3 py-3 text-center text-surface-400">No component changed.</TableCell>
              </TableRow>
            )}
            <TableRow>
              <TableCell colSpan={colCount} className="border-t-2 border-surface-200 px-3 pt-3 text-[10px] font-bold uppercase tracking-[0.06em] text-surface-500">Total</TableCell>
            </TableRow>
            {totalRows.map(t => (
              <TableRow key={t.label} className="font-semibold text-surface-900">
                <TableCell className="px-3">{t.label}</TableCell>
                {before && <TableCell className="px-3 text-right tabular-nums">₹{fmtAmt(t.before ?? 0)}</TableCell>}
                <TableCell className="px-3 text-right tabular-nums">₹{fmtAmt(t.after)}</TableCell>
                <TableCell className="px-3 text-right tabular-nums font-normal text-surface-500">{t.monthly}</TableCell>
                {before && <TableCell className="px-3 text-right tabular-nums"><Change before={t.before} after={t.after} /></TableCell>}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      </>
      )}
    </div>
  );
}
